import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import PageLayout from "./components/PageLayout";

function BuildInvestment() {
  const { username, carId } = useParams();

  const [car, setCar] = useState(null);

  useEffect(() => {
    const loadCar = async () => {
      const docRef = doc(db, "profiles", username.toLowerCase());
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();

        const foundCar = data.garageCars?.find(
          (item) => String(item.id) === String(carId)
        );

        if (foundCar) {
          setCar(foundCar);
        }
      }
    };

    loadCar();
  }, [username, carId]);

  const getPriceNumber = (price) => {
    if (price === null || price === undefined || price === "") return 0;

    return Number(String(price).replace(/[^0-9.]/g, "")) || 0;
  };

  const getModInvestment = (mod) => {
    return getPriceNumber(mod.totalCost ?? mod.price);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0
    }).format(getPriceNumber(value));
  };

  if (!car) {
    return (
      <PageLayout maxWidth="700px" showBack>
        <div style={loadingStyle}>Loading investment...</div>
      </PageLayout>
    );
  }

  if (!car.showBuildCost) {
    return (
      <PageLayout maxWidth="700px" showBack>
        <div style={loadingStyle}>Build investment is private.</div>
      </PageLayout>
    );
  }

  const pricedMods =
    car.mods?.filter((mod) => getModInvestment(mod) > 0) || [];

  const totalInvestment = pricedMods.reduce(
    (total, mod) => total + getModInvestment(mod),
    0
  );

  const pricedModCount = pricedMods.length;
  const averageModCost =
    pricedModCount > 0 ? totalInvestment / pricedModCount : 0;

  const highestCostMod = pricedMods.reduce((highest, mod) => {
    if (!highest) return mod;

    return getModInvestment(mod) > getModInvestment(highest) ? mod : highest;
  }, null);

  const groupedMods = {};
  const categoryTotals = {};

  pricedMods.forEach((mod) => {
    if (!groupedMods[mod.category]) {
      groupedMods[mod.category] = [];
      categoryTotals[mod.category] = 0;
    }

    groupedMods[mod.category].push(mod);
    categoryTotals[mod.category] += getModInvestment(mod);
  });

  return (
    <PageLayout
      title="Build Investment"
      subtitle={`${car.year} ${car.make} ${car.model}`}
      maxWidth="700px"
      showBack
    >
      <div style={totalBoxStyle}>
        <p style={totalLabelStyle}>Total Build Investment</p>
        <h2 style={totalValueStyle}>{formatCurrency(totalInvestment)}</h2>

        <div style={summaryGridStyle}>
          <div style={summaryStatStyle}>
            <p style={summaryStatLabelStyle}>Priced Mods</p>
            <strong style={summaryStatValueStyle}>{pricedModCount}</strong>
          </div>

          <div style={summaryStatStyle}>
            <p style={summaryStatLabelStyle}>Average Per Mod</p>
            <strong style={summaryStatValueStyle}>
              {formatCurrency(averageModCost)}
            </strong>
          </div>

          <div style={summaryStatStyle}>
            <p style={summaryStatLabelStyle}>Highest-Cost Mod</p>
            <strong style={summaryStatValueStyle}>
              {highestCostMod?.partName || "None"}
            </strong>

            {highestCostMod && (
              <span style={summaryStatMetaStyle}>
                {formatCurrency(getModInvestment(highestCostMod))}
              </span>
            )}
          </div>
        </div>
      </div>

      {pricedMods.length === 0 ? (
        <div style={emptyStateStyle}>
          <h3 style={emptyStateTitleStyle}>No investment data yet.</h3>
          <p style={emptyStateTextStyle}>
            Add prices or total costs to your mods to build an investment breakdown.
          </p>
        </div>
      ) : (
        Object.entries(groupedMods).map(([category, mods]) => {
          const categoryTotal = categoryTotals[category] || 0;
          const categoryPercent =
            totalInvestment > 0
              ? Math.round((categoryTotal / totalInvestment) * 100)
              : 0;

          return (
            <div key={category} style={categoryBoxStyle}>
              <div style={categoryHeaderStyle}>
                <div>
                  <h3 style={categoryTitleStyle}>{category}</h3>

                  {totalInvestment > 0 && (
                    <p style={categoryPercentStyle}>
                      {categoryPercent}% of total build investment
                    </p>
                  )}
                </div>

                <strong style={categoryTotalStyle}>
                  {formatCurrency(categoryTotal)}
                </strong>
              </div>

              {mods.map((mod) => {
                const partCost = getPriceNumber(mod.price);
                const laborCost = getPriceNumber(mod.laborCost);
                const rowTotal = getModInvestment(mod);

                return (
                  <div key={mod.id} style={modRowStyle}>
                    <div style={modInfoStyle}>
                      <strong style={modTitleStyle}>{mod.partName}</strong>

                      {mod.brand && (
                        <p style={mutedTextStyle}>{mod.brand}</p>
                      )}

                      {mod.partNumber && (
                        <p style={mutedTextStyle}>
                          Part #: {mod.partNumber}
                        </p>
                      )}

                      {partCost > 0 && (
                        <p style={mutedTextStyle}>
                          Part: {formatCurrency(partCost)}
                        </p>
                      )}

                      {laborCost > 0 && (
                        <p style={mutedTextStyle}>
                          Labor: {formatCurrency(laborCost)}
                        </p>
                      )}
                    </div>

                    <strong style={modTotalStyle}>
                      {formatCurrency(rowTotal)}
                    </strong>
                  </div>
                );
              })}
            </div>
          );
        })
      )}
    </PageLayout>
  );
}

const loadingStyle = {
  color: "white",
  textAlign: "center",
  padding: "40px"
};

const totalBoxStyle = {
  backgroundColor: "#1f1f1f",
  border: "1px solid #333",
  borderRadius: "16px",
  padding: "20px",
  marginBottom: "20px",
  boxSizing: "border-box"
};

const totalLabelStyle = {
  opacity: 0.7,
  margin: 0,
  fontSize: "13px",
  fontWeight: "800",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  color: "#e10600"
};

const totalValueStyle = {
  margin: "8px 0 18px",
  color: "white",
  fontSize: "34px",
  lineHeight: "1.1"
};

const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
  gap: "10px"
};

const summaryStatStyle = {
  backgroundColor: "#181818",
  border: "1px solid #303030",
  borderRadius: "12px",
  padding: "12px",
  boxSizing: "border-box",
  minWidth: 0
};

const summaryStatLabelStyle = {
  margin: "0 0 6px",
  color: "#e10600",
  fontSize: "12px",
  fontWeight: "800",
  textTransform: "uppercase",
  letterSpacing: "0.5px"
};

const summaryStatValueStyle = {
  display: "block",
  color: "white",
  fontSize: "18px",
  lineHeight: "1.25",
  overflowWrap: "anywhere"
};

const summaryStatMetaStyle = {
  display: "block",
  color: "#e10600",
  fontSize: "13px",
  fontWeight: "900",
  marginTop: "5px"
};

const categoryBoxStyle = {
  backgroundColor: "#1c1c1c",
  border: "1px solid #2a2a2a",
  borderRadius: "16px",
  padding: "18px",
  marginBottom: "16px",
  boxSizing: "border-box"
};

const categoryHeaderStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "12px",
  flexWrap: "wrap",
  borderBottom: "1px solid #333",
  paddingBottom: "12px",
  marginBottom: "4px"
};

const categoryTitleStyle = {
  margin: 0,
  color: "#e10600",
  fontSize: "18px",
  lineHeight: "1.25"
};

const categoryTotalStyle = {
  color: "#e10600",
  fontSize: "18px",
  lineHeight: "1.25",
  whiteSpace: "nowrap"
};

const categoryPercentStyle = {
  margin: "6px 0 0",
  color: "#aaa",
  fontSize: "13px",
  lineHeight: "1.4"
};

const modRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "12px",
  borderBottom: "1px solid #333",
  padding: "12px 0",
  minWidth: 0
};

const modInfoStyle = {
  minWidth: 0,
  overflowWrap: "anywhere"
};

const modTitleStyle = {
  color: "white",
  overflowWrap: "anywhere"
};

const modTotalStyle = {
  color: "white",
  whiteSpace: "nowrap",
  flexShrink: 0
};

const mutedTextStyle = {
  opacity: 0.7,
  margin: "6px 0 0",
  overflowWrap: "anywhere"
};

const emptyStateStyle = {
  backgroundColor: "#1c1c1c",
  border: "1px solid #2a2a2a",
  borderRadius: "16px",
  padding: "22px",
  textAlign: "center",
  boxSizing: "border-box"
};

const emptyStateTitleStyle = {
  margin: "0 0 8px",
  color: "white",
  fontSize: "20px"
};

const emptyStateTextStyle = {
  margin: 0,
  color: "#aaa",
  lineHeight: "1.6"
};

export default BuildInvestment;